import type { ReactNode } from 'react';
import type { ConnectionPlayer } from 'types/connections';
import { pluralize } from 'utils/shared/pluralize';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import ShieldCheckTrusted from 'svg/ShieldCheckTrusted';
import BadgeRating from 'components/BadgeRating';
import classNames from 'styles/utils/classNames';

interface Props {
  player: ConnectionPlayer;
  /** Button(s) pinned to the right of the row. */
  action?: ReactNode;
  /** Overflow trigger rendered after `action`. */
  menu?: ReactNode;
  /** Swaps the "N games together" line for the player's @username. */
  isShowUsername?: boolean;
  /** Brand ring around the avatar — the "Top connections" treatment. */
  isHighlighted?: boolean;
  className?: string;
}

export default function PlayerRow({
  player,
  action,
  menu,
  isShowUsername = false,
  isHighlighted = false,
  className,
}: Props) {
  return (
    <div className={classNames('flex items-center gap-ds-md', className)}>
      <div className="flex min-w-0 flex-1 items-center gap-ds-sm">
        {/* 44px frame: the 40px avatar sits top-left, the shield fills the corner. */}
        <div className="relative size-11 shrink-0">
          <img
            src={getProfileImageUrlOrPlaceholder({ path: player.profileImagePath })}
            alt=""
            className={classNames(
              'absolute left-0 top-0 size-10 rounded-full object-cover',
              isHighlighted && 'border-[0.1875rem] border-brand-fire-600',
            )}
          />
          {player.isVerified && (
            <span className="absolute bottom-0 right-0 rounded-full bg-color-bg-lightmode-primary p-[0.1rem] dark:bg-color-bg-darkmode-primary">
              <ShieldCheckTrusted className="block size-[0.8rem]" />
            </span>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-ds-xs">
          <p className="typography-product-card-title-mobile truncate text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {player.fullName}
          </p>
          {/* No badge when the player hasn't rated themselves for their sport. */}
          {!!player.skillLevel && (
            <BadgeRating
              level={player.skillLevel}
              sport={player.sport}
              variant="neutral"
              size="xs"
              className="h-4 gap-0.5 self-start px-ds-xs"
            />
          )}
          <p className="typography-product-text-card-mobile truncate text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            {isShowUsername
              ? `@${player.username}`
              : `${pluralize({ count: player.gamesTogether, singular: 'game' })} together`}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-ds-sm">
        {action}
        {menu}
      </div>
    </div>
  );
}
